import { OrderNumberType, type OrderNumber } from '@projects/types/orderNumbers';
import type { PointElement, SplineElement } from '@elements/types';

export const getScanIdsForLabeledElement = (
  element: Pick<PointElement, 'pointId'> | Pick<SplineElement, 'fromPoint' | 'toPoint'> | undefined,
  pointEntities: Record<string, { scanId?: string } | undefined>,
): string[] => {
  if (!element) return [];

  const pointIds =
    'pointId' in element ? [element.pointId] : [element.fromPoint?.id, element.toPoint?.id];

  return [
    ...new Set(
      pointIds
        .filter((pointId): pointId is string => Boolean(pointId))
        .map((pointId) => pointEntities[pointId]?.scanId)
        .filter((scanId): scanId is string => Boolean(scanId)),
    ),
  ];
};

export interface ScanSmaData {
  orderNumbers: readonly OrderNumber[];
  /** False when the scan has no SMA information, rather than an explicit empty SMA list. */
  hasSmaInformation: boolean;
}

export const doesLabeledSmaMismatchAnyScan = (
  scanSmaData: readonly ScanSmaData[],
  labeledOrderNumbers: readonly OrderNumber[],
): boolean => {
  const labeledSmaValues = new Set(
    labeledOrderNumbers
      .filter(({ type }) => type === OrderNumberType.SMA)
      .map(({ value }) => value),
  );

  return scanSmaData.some(({ orderNumbers, hasSmaInformation }) => {
    if (!hasSmaInformation) return labeledSmaValues.size > 0;

    const scanSmaValues = new Set(
      orderNumbers.filter(({ type }) => type === OrderNumberType.SMA).map(({ value }) => value),
    );

    // The element may have fewer SMAs than its scan, but each element SMA
    // must be present on every scan associated with that data.
    return [...labeledSmaValues].some((value) => !scanSmaValues.has(value));
  });
};
